import {Routes} from '@src/Constant/routes';

export interface AccessRule {
  type: 'page' | 'action' | 'element';
  identifier: string | Routes;
  requiresAuth: boolean;
  requiresPremium?: boolean;
  description?: string;
}

/**
 * Configuration centralisée des règles d'accès
 * Définit quelles pages, actions et éléments nécessitent une authentification
 */
export const ACCESS_RULES: AccessRule[] = [
  // === PAGES NÉCESSITANT UNE AUTHENTIFICATION ===
  {
    type: 'page',
    identifier: Routes.PROFILE_HOME,
    requiresAuth: true,
    description: 'Page de profil utilisateur',
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_MY_PROFILE,
    requiresAuth: true,
    description: 'Mes informations personnelles',
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_SUBSCRIPTION,
    requiresAuth: true,
    requiresPremium: true,
    description: "Gestion de l'abonnement",
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_SETTINGS,
    requiresAuth: true,
    description: 'Paramètres du compte',
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_ACHIEVEMENT,
    requiresAuth: true,
    description: 'Mes réussites',
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_NOTIFICATION,
    requiresAuth: true,
    description: 'Paramètres de notification',
  },
  {
    type: 'page',
    identifier: Routes.BOOKMARKS,
    requiresAuth: true,
    description: 'Mes favoris/signets',
  },
  {
    type: 'page',
    identifier: Routes.PROFILE_OPPORTUNITIES,
    requiresAuth: true,
    description: 'Mes opportunités',
  },
  {
    type: 'page',
    identifier: Routes.PROMOCODE,
    requiresAuth: true,
    description: 'Page de code promo',
  },

  // 🎓 Routes de cours nécessitant une authentification
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_SINGLE,
  //   requiresAuth: true,
  //   description: 'Accès aux cours'
  // },
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_UNIT,
  //   requiresAuth: true,
  //   description: 'Accès aux unités de cours'
  // },
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_CATEGORY,
  //   requiresAuth: true,
  //   description: 'Catégories de cours'
  // },
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_CATEGORY_HOME,
  //   requiresAuth: true,
  //   description: 'Page d\'accueil des catégories de cours'
  // },
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_SINGLE,
  //   requiresAuth: true,
  //   description: 'Cours individuels avec préfixe /app (basé sur COURSE_SINGLE)'
  // },
  // {
  //   type: 'page',
  //   identifier: Routes.COURSE_UNIT,
  //   requiresAuth: true,
  //   description: 'Unités de cours avec préfixe /app (basé sur COURSE_UNIT)'
  // },

  // 🎬 Players audio et vidéo nécessitant une authentification
  {
    type: 'page',
    identifier: Routes.WATCH,
    requiresAuth: true,
    description: 'Lecteur vidéo',
  },
  {
    type: 'page',
    identifier: Routes.RADIO_PLAYER,
    requiresAuth: true,
    description: 'Lecteur radio',
  },
  {
    type: 'page',
    identifier: Routes.PODCAST_PLAYER,
    requiresAuth: true,
    description: 'Lecteur podcast',
  },
  {
    type: 'page',
    identifier: Routes.LISTEN_REDIRECTION,
    requiresAuth: true,
    description: 'Redirection lecteur audio',
  },
  {
    type: 'page',
    identifier: Routes.PLAYER_QUIZ_SURVEY,
    requiresAuth: true,
    description: 'Lecteur quiz/sondage',
  },

  // 📰 Actualités nécessitant une authentification
  // {
  //   type: 'page',
  //   identifier: Routes.NEWS_HOME,
  //   requiresAuth: true,
  //   description: 'Page d\'accueil des actualités'
  // },
  {
    type: 'page',
    identifier: Routes.NEWS_SINGLE,
    requiresAuth: true,
    description: "Article d'actualité individuel",
  },

  // 💼 Emplois nécessitant une authentification
  {
    type: 'page',
    identifier: Routes.SINGLE_JOB,
    requiresAuth: true,
    description: "Détails d'un emploi",
  },

  // 🎯 Routes de test pour démontrer l'auth des utilisateurs anonymes
  {
    type: 'page',
    identifier: '/app/test-protected',
    requiresAuth: true,
    description: 'Page de test protégée',
  },

  // === ACTIONS NÉCESSITANT UNE AUTHENTIFICATION ===
  {
    type: 'action',
    identifier: 'bookmark-add',
    requiresAuth: true,
    description: 'Ajouter aux favoris',
  },
  {
    type: 'action',
    identifier: 'bookmark-remove',
    requiresAuth: true,
    description: 'Retirer des favoris',
  },
  {
    type: 'action',
    identifier: 'like-content',
    requiresAuth: true,
    description: 'Liker du contenu',
  },
  {
    type: 'action',
    identifier: 'comment-add',
    requiresAuth: true,
    description: 'Ajouter un commentaire',
  },
  {
    type: 'action',
    identifier: 'playlist-create',
    requiresAuth: true,
    description: 'Créer une playlist',
  },
  {
    type: 'action',
    identifier: 'playlist-add-content',
    requiresAuth: true,
    description: 'Ajouter à une playlist',
  },
  {
    type: 'action',
    identifier: 'profile-update',
    requiresAuth: true,
    description: 'Modifier le profil',
  },

  // 📰 Actions GraphQL pour les actualités nécessitant une authentification
  {
    type: 'action',
    identifier: 'news-access',
    requiresAuth: true,
    description:
      "Accès aux requêtes GraphQL d'actualités (NewsByUrlEndUrl, NewsByUrl, NewsById, etc.)",
  },
  {
    type: 'action',
    identifier: 'notification-settings',
    requiresAuth: true,
    description: 'Gérer les notifications',
  },
  {
    type: 'action',
    identifier: 'download-content',
    requiresAuth: true,
    requiresPremium: true,
    description: 'Télécharger du contenu',
  },
  {
    type: 'action',
    identifier: 'premium-content-access',
    requiresAuth: true,
    requiresPremium: true,
    description: 'Accéder au contenu premium',
  },

  // 🎓 Actions spécifiques aux cours (COURSE_SINGLE sécurisé)
  {
    type: 'action',
    identifier: 'course-access',
    requiresAuth: true,
    description: 'Accéder aux cours',
  },
  {
    type: 'action',
    identifier: 'course-progress',
    requiresAuth: true,
    description: 'Enregistrer la progression dans un cours',
  },
  {
    type: 'action',
    identifier: 'course-completion',
    requiresAuth: true,
    description: 'Marquer un cours comme terminé',
  },
  {
    type: 'action',
    identifier: 'exam-submission',
    requiresAuth: true,
    description: 'Soumettre un examen',
  },
  {
    type: 'action',
    identifier: 'unit-progression',
    requiresAuth: true,
    description: 'Progression dans les unités de cours',
  },
  {
    type: 'action',
    identifier: 'course-status-update',
    requiresAuth: true,
    description: "Mettre à jour le statut d'un cours",
  },

  // 🎵 Actions spécifiques aux players audio/vidéo
  {
    type: 'action',
    identifier: 'player-access',
    requiresAuth: true,
    description: 'Accéder aux lecteurs audio/vidéo',
  },
  {
    type: 'action',
    identifier: 'audio-play',
    requiresAuth: true,
    description: 'Lire du contenu audio',
  },
  {
    type: 'action',
    identifier: 'video-play',
    requiresAuth: true,
    description: 'Lire du contenu vidéo',
  },
  {
    type: 'action',
    identifier: 'player-progress',
    requiresAuth: true,
    description: 'Sauvegarder la progression de lecture',
  },
  {
    type: 'action',
    identifier: 'player-quality-change',
    requiresAuth: true,
    description: 'Changer la qualité de lecture',
  },
  {
    type: 'action',
    identifier: 'player-speed-change',
    requiresAuth: true,
    description: 'Changer la vitesse de lecture',
  },

  // 🎯 Actions spécifiques aux quiz/surveys
  {
    type: 'action',
    identifier: 'quiz-access',
    requiresAuth: true,
    description: 'Accéder aux quiz',
  },
  {
    type: 'action',
    identifier: 'survey-access',
    requiresAuth: true,
    description: 'Accéder aux enquêtes',
  },

  // 💼 Actions spécifiques aux jobs
  {
    type: 'action',
    identifier: 'job-access',
    requiresAuth: true,
    description: "Accéder aux offres d'emploi",
  },

  // 👤 Actions spécifiques au profil
  {
    type: 'action',
    identifier: 'profile-access',
    requiresAuth: true,
    description: 'Accéder au profil utilisateur',
  },
  {
    type: 'action',
    identifier: 'bookmark-access',
    requiresAuth: true,
    description: 'Accéder aux favoris',
  },

  // === ÉLÉMENTS UI NÉCESSITANT UNE AUTHENTIFICATION ===

  // 🎯 Approche moderne : Utiliser data-action comme standard principal
  {
    type: 'element',
    identifier: '[data-action="bookmark"]',
    requiresAuth: true,
    description: 'Actions de favoris/bookmarks (standard moderne)',
  },
  {
    type: 'element',
    identifier: '[data-action="like"]',
    requiresAuth: true,
    description: 'Actions de like (standard moderne)',
  },
  {
    type: 'element',
    identifier: '[data-action="comment"]',
    requiresAuth: true,
    description: 'Actions de commentaire (standard moderne)',
  },
  {
    type: 'element',
    identifier: '[data-action="download"]',
    requiresAuth: true,
    requiresPremium: true,
    description: 'Actions de téléchargement (standard moderne)',
  },
  {
    type: 'element',
    identifier: '[data-action="share"]',
    requiresAuth: true,
    description: 'Actions de partage (standard moderne)',
  },

  // 🔒 Marqueurs génériques d'authentification
  {
    type: 'element',
    identifier: '[data-auth-required="true"]',
    requiresAuth: true,
    description: 'Éléments marqués comme nécessitant une auth',
  },
  {
    type: 'element',
    identifier: '[data-premium-required="true"]',
    requiresAuth: true,
    requiresPremium: true,
    description: 'Éléments marqués comme nécessitant un abonnement premium',
  },

  // 🔄 Support legacy des classes CSS (à migrer progressivement vers data-action)
  {
    type: 'element',
    identifier: '.bookmark-button',
    requiresAuth: true,
    description:
      'Boutons de favoris (legacy - migrer vers data-action="bookmark")',
  },
  {
    type: 'element',
    identifier: '.like-button',
    requiresAuth: true,
    description: 'Boutons de like (legacy - migrer vers data-action="like")',
  },
  {
    type: 'element',
    identifier: '.favoris-button',
    requiresAuth: true,
    description:
      'Boutons de favoris FR (legacy - migrer vers data-action="bookmark")',
  },
  {
    type: 'element',
    identifier: '.comment-button',
    requiresAuth: true,
    description:
      'Boutons de commentaire (legacy - migrer vers data-action="comment")',
  },
  {
    type: 'element',
    identifier: '.download-button',
    requiresAuth: true,
    requiresPremium: true,
    description:
      'Boutons de téléchargement (legacy - migrer vers data-action="download")',
  },
];

/**
 * Convertit un pattern de route en regex
 * Permet aux routes de matcher même avec des préfixes et slashes de fin
 */
const routePatternToRegex = (pattern: string): RegExp => {
  // D'abord remplacer les wildcards * par un placeholder temporaire
  let regexPattern = pattern.replace(/\*/g, '__WILDCARD__');

  // Échapper les caractères spéciaux sauf :
  regexPattern = regexPattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

  // Remplacer les paramètres :param par [^/]+
  regexPattern = regexPattern.replace(/:[\w]+/g, '[^/]+');

  // Remplacer les placeholders par .*
  regexPattern = regexPattern.replace(/__WILDCARD__/g, '.*');

  // Rendre le slash de fin optionnel pour être plus permissif
  if (regexPattern.endsWith('/')) {
    regexPattern = regexPattern.slice(0, -1) + '/?';
  } else {
    regexPattern = regexPattern + '/?';
  }

  // Si le pattern commence par un wildcard explicite, garder l'ancrage au début
  if (pattern.startsWith('*')) {
    regexPattern = `^${regexPattern}$`;
  } else {
    // Sinon, permettre n'importe quel préfixe mais ancrer à la fin
    regexPattern = `.*${regexPattern}$`;
  }

  return new RegExp(regexPattern);
};

/**
 * Vérifie si une route correspond à un pattern
 */
const matchesRoutePattern = (
  route: string,
  pattern: string | undefined,
): boolean => {
  // Vérifier que pattern est défini
  if (!pattern || typeof pattern !== 'string') {
    console.warn('[AccessRules] Invalid pattern:', pattern);
    return false;
  }

  // Cas 1: Correspondance exacte
  if (route === pattern) {
    return true;
  }

  // Cas 2: Pattern avec wildcards ou paramètres
  if (pattern.includes(':') || pattern.includes('*')) {
    const regex = routePatternToRegex(pattern);
    return regex.test(route);
  }

  // Cas 3: Pattern simple (pour compatibilité avec l'ancien système)
  if (route.includes(pattern)) {
    return true;
  }

  return false;
};

/**
 * Vérifie si une page nécessite une authentification
 */
export const pageRequiresAuth = (route?: string): AccessRule | null => {
  // Nettoyer la route (enlever les paramètres, etc.)
  const cleanRoute = route?.split('?')[0].split('#')[0];

  console.log('🔍 [AccessRules] Checking route:', cleanRoute);

  // Retourner null si pas de route
  if (!cleanRoute) return null;

  const matchingRule = ACCESS_RULES.find(rule => {
    if (rule.type !== 'page') return false;

    const pattern = String(rule.identifier);
    const matches = matchesRoutePattern(cleanRoute, pattern);

    if (matches) {
      console.log('✅ [AccessRules] Route matches pattern:', {
        route: cleanRoute,
        pattern,
        rule: rule.description,
      });
    }

    return matches;
  });

  if (!matchingRule) {
    console.log(
      '❌ [AccessRules] No matching rule found for route:',
      cleanRoute,
    );
  }

  return matchingRule || null;
};

/**
 * Vérifie si une action nécessite une authentification
 */
export const actionRequiresAuth = (actionId: string): AccessRule | null => {
  return (
    ACCESS_RULES.find(
      rule => rule.type === 'action' && rule.identifier === actionId,
    ) || null
  );
};

/**
 * Vérifie si un élément DOM nécessite une authentification
 */
export const elementRequiresAuth = (
  element: HTMLElement,
): AccessRule | null => {
  return (
    ACCESS_RULES.find(rule => {
      if (rule.type !== 'element') return false;

      const selector = rule.identifier as string;

      // 🎯 Priorité 1: Vérifier les data-action (approche moderne)
      if (selector.includes('[data-action=')) {
        const actionMatch = selector.match(/\[data-action="([^"]+)"\]/);
        if (actionMatch && element.dataset.action === actionMatch[1]) {
          return true;
        }
      }

      // 🔒 Priorité 2: Vérifier les marqueurs d'authentification
      if (
        selector.includes('[data-auth-required="true"]') &&
        element.dataset.authRequired === 'true'
      ) {
        return true;
      }

      if (
        selector.includes('[data-premium-required="true"]') &&
        element.dataset.premiumRequired === 'true'
      ) {
        return true;
      }

      // 🔄 Priorité 3: Support legacy des classes CSS
      if (selector.startsWith('.')) {
        const className = selector.substring(1);
        if (element.classList.contains(className)) {
          return true;
        }
      }

      // 🧩 Priorité 4: Sélecteurs complexes (pour compatibilité)
      if (selector.includes('[data-component=')) {
        try {
          // Utiliser matches() pour les sélecteurs complexes
          if (element.matches && element.matches(selector)) {
            return true;
          }
        } catch (error) {
          // Ignorer les erreurs de sélecteur invalide
          console.warn('[AccessRules] Invalid selector:', selector, error);
        }
      }

      return false;
    }) || null
  );
};
